import { T } from '@/lib/theme'
import { Panel } from '@/components/ui'
import { fmtUsd, fmtPct, fmtBps } from '@/lib/utils'
import { useHistoricalStats } from '@/hooks/useHistoricalStats'
import { useProtocolState } from '@/hooks/useProtocolState'
import { MiniLineChart } from '@/components/dashboard/MiniLineChart'

/// % change from the first loaded history point to the current one - NOT a fixed 24h/7d window
/// the way the reference dashboard shows, since this app has no periodic snapshot infrastructure
/// to back that with real numbers. Labeled "since history start" rather than implying a time
/// window this doesn't actually track. Change from an exact-zero baseline is undefined (not
/// "-100%" or "+Infinity%"), so that case renders as null and the caller shows "New" instead.
function pctChange(first, last) {
  if (first === 0) return null
  return ((last - first) / first) * 100
}

function ChangeBadge({ pct }) {
  if (pct === null) {
    return <span style={{ ...badge, background: T.cyanDim, color: T.cyan }}>New</span>
  }
  const positive = pct >= 0
  return (
    <span style={{ ...badge, background: positive ? T.positiveDim : T.burnDim, color: positive ? T.positive : T.negative }}>
      {positive ? '+' : ''}{fmtPct(pct)}
    </span>
  )
}

/// "Total Deposit vs Total Borrow" dashboard hero, reference-styled off Anchor's own dashboard -
/// real numbers throughout: current totals from useProtocolState (interest-inclusive, live,
/// summed across every reserve), the chart and % change badges from useHistoricalStats' event-
/// sourced series (principal-only, no fabricated/interpolated points - see that hook's own doc
/// comment). The market row below is now per-reserve (was a single hardcoded "Stablecoin Market"
/// row before this became a symmetric multi-reserve pool) - each reserve supplies AND borrows
/// now, so each gets its own Deposit/Borrow/APY/APR row instead of a separate "ETH Market" panel
/// living elsewhere on the page.
export function StatsMarketPanel() {
  const { series, loading } = useHistoricalStats()
  const { d } = useProtocolState()
  const eth = d.reserves.find((r) => r.key === 'eth')
  const usdx = d.reserves.find((r) => r.key === 'usdx')

  const hasHistory = series && series.length > 1
  const depositNow = d.reserves.reduce((sum, r) => sum + r.totalSupplied * r.priceUsd, 0)
  const borrowNow = d.reserves.reduce((sum, r) => sum + r.totalBorrows * r.priceUsd, 0)

  // History series are native-unit (tvlUsdx/tvlCollateralEth, borrowedUsdx/borrowedCollateralEth)
  // - convert the ETH-denominated legs through the CURRENT price (no historical price feed
  // reconstruction, same "approx." caveat useHistoricalStats' own doc comment states) before
  // combining into one USD-denominated pair of lines.
  const depositChangePct = hasHistory
    ? pctChange(
        series[0].tvlUsdx + series[0].tvlCollateralEth * (eth?.priceUsd ?? 0),
        series[series.length - 1].tvlUsdx + series[series.length - 1].tvlCollateralEth * (eth?.priceUsd ?? 0),
      )
    : null
  const borrowChangePct = hasHistory
    ? pctChange(
        series[0].borrowedUsdx + series[0].borrowedCollateralEth * (eth?.priceUsd ?? 0),
        series[series.length - 1].borrowedUsdx + series[series.length - 1].borrowedCollateralEth * (eth?.priceUsd ?? 0),
      )
    : null

  let depositValues = [], borrowValues = []
  if (hasHistory) {
    depositValues = series.map((p) => p.tvlUsdx + p.tvlCollateralEth * (eth?.priceUsd ?? 0))
    borrowValues = series.map((p) => p.borrowedUsdx + p.borrowedCollateralEth * (eth?.priceUsd ?? 0))
  }

  return (
    <Panel style={{ padding: 32 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: T.positive, display: 'inline-block' }} />
            <span style={label}>Total Deposit</span>
            <ChangeBadge pct={depositChangePct} />
          </div>
          <div className="mono" style={{ marginTop: 6, fontSize: 32, fontWeight: 700, color: T.text, fontFamily: T.fontN }}>
            {fmtUsd(depositNow)}
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: T.accent, display: 'inline-block' }} />
            <span style={label}>Total Borrow</span>
            <ChangeBadge pct={borrowChangePct} />
          </div>
          <div className="mono" style={{ marginTop: 6, fontSize: 32, fontWeight: 700, color: T.text, fontFamily: T.fontN }}>
            {fmtUsd(borrowNow)}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        {loading ? (
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '40px 0' }}>Loading history…</div>
        ) : !hasHistory ? (
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '40px 0' }}>
            Not enough on-chain activity yet to chart.
          </div>
        ) : (
          <MiniLineChart
            series={series}
            lines={[
              { values: depositValues, color: T.positive },
              { values: borrowValues, color: T.accent },
            ]}
          />
        )}
      </div>

      {/* ── Per-reserve market rows ─────────────────────────────────── */}
      <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
        <div style={marketHeaderRow}>
          <span style={label}>Market</span>
          <span style={{ ...label, textAlign: 'right' }}>Total Deposit</span>
          <span style={{ ...label, textAlign: 'right' }}>Deposit APY</span>
          <span style={{ ...label, textAlign: 'right' }}>Total Borrow</span>
          <span style={{ ...label, textAlign: 'right' }}>Borrow APR</span>
        </div>
        {d.reserves.map((r) => (
          <div key={r.key} style={marketRow}>
            <span style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{r.symbol}</span>
            <span className="mono" style={marketCell}>{fmtUsd(r.totalSupplied * r.priceUsd)}</span>
            <span className="mono" style={{ ...marketCell, color: T.positive }}>{fmtBps(r.totalSupplyAprBps)}</span>
            <span className="mono" style={marketCell}>{fmtUsd(r.totalBorrows * r.priceUsd)}</span>
            <span className="mono" style={{ ...marketCell, color: r.totalBorrowAprBps < 0 ? T.positive : T.text }}>{fmtBps(r.totalBorrowAprBps)}</span>
          </div>
        ))}
      </div>
    </Panel>
  )
}

const label = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
}
const badge = {
  padding: '2px 8px', borderRadius: T.r.pill, fontFamily: T.fontM, fontSize: 11, fontWeight: 700,
}
// Fixed-width columns, not content-sized (`minmax(90px, auto)` let each column stretch to its
// widest value across rows - e.g. "17520.11%" vs "943.57%" in the same column - which threw off
// the visual gap between columns row to row even though the grid boundaries themselves lined up).
const marketHeaderRow = {
  display: 'grid', gridTemplateColumns: '1fr repeat(4, 130px)', gap: 16,
  paddingBottom: 10, borderBottom: `1px solid ${T.border}`,
}
const marketRow = {
  display: 'grid', gridTemplateColumns: '1fr repeat(4, 130px)', gap: 16,
  padding: '14px 0 0', alignItems: 'center',
}
const marketCell = {
  textAlign: 'right', color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
}
